// react
import { useContext } from 'react';

// router
import { useParams, useSearchParams } from 'react-router-dom';

// types
import { Country, Frame, User, UserProject } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextProject, ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// mui
import { Box, Grid, Paper, Typography } from '@mui/material';

// dialogs
import { DialogEditUser } from '../../dialogs/DialogEditUser/DialogEditUser';
import { DialogUpdateUserEntities } from '../../dialogs/DialogUpdateUserEntities/DialogUpdateUserEntities';
import { DialogUpdateUserStatus } from '../../dialogs/DialogUpdateUserStatus/DialogUpdateUserStatus';

// ui
import { BtnMenu } from '../../ui/BtnMenu/BtnMenu';
import { ChipStatus } from '../../ui/ChipStatus/ChipStatus';
import { IconCircle } from '../../ui/IconCircle/IconCircle';
import { MenuItemProps } from '../../ui/Menu/Menu';
import { RowKeyValue } from '../../ui/RowKeyValue/RowKeyValue';
import { Time } from '../../ui/Time/Time';
import { TypographyCaption } from '../../ui/TypographyCaption/TypographyCaption';

// links
import { LinkEmail } from '../../links/LinkEmail/LinkEmail';
import { LinkPhone } from '../../links/LinkPhone/LinkPhone';

// icons
import {
	DoDisturbOutlined as IconDoDisturb,
	ModeEditOutlined as IconModeEdit,
	PersonRemoveOutlined as IconPersonRemove,
	PowerSettingsNewOutlined as IconPowerSettingsNew,
	SendOutlined as IconSend,
} from '@mui/icons-material';

// props
export interface CardUserProps {
	frame?: Frame;
	refetchUsers?: () => void;
	user: User;
}

export const CardUser = ({ frame, refetchUsers, user }: CardUserProps) => {
	// hooks
	const { idBeneficiary, idEntity, idSupplier } = useParams();
	const { t } = useTranslation();

	// state
	const [searchParams, setSearchParams] = useSearchParams();

	// context
	const { project } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogUser = searchParams.get('dialogUser');

	// vars
	const entityId = idBeneficiary || idEntity || idSupplier;
	const lastUpdated: Date = new Date(user.modified || user.created);

	// get user's status for current project
	const userProject = (user.projects as UserProject[])?.find((p) => p.id === project?.id);
	const userStatus = userProject?.status;

	// handlers
	const handleDeactivateUser = () => {
		searchParams.set('dialog', 'updateUserStatus');
		searchParams.set('dialogUser', String(user.id));
		searchParams.set('dialogStatus', 'DEACTIVATED');
		setSearchParams(searchParams);
	};

	const handleActivateUser = () => {
		let statusNew = 'INVITED';

		if (frame === 'ENTITY') {
			statusNew = 'ACTIVE';
		}

		searchParams.set('dialog', 'updateUserStatus');
		searchParams.set('dialogUser', String(user.id));
		searchParams.set('dialogStatus', statusNew);
		setSearchParams(searchParams);
	};

	const handleEditUser = () => {
		searchParams.set('dialog', 'editUser');
		searchParams.set('dialogUser', String(user.id));
		setSearchParams(searchParams);
	};

	const handleRemoveUser = () => {
		searchParams.set('dialog', 'removeEntityUser');
		searchParams.set('dialogUser', String(user.id));
		setSearchParams(searchParams);
	};

	const handleResendInvite = () => {
		searchParams.set('dialog', 'updateUserStatus');
		searchParams.set('dialogUser', String(user.id));
		searchParams.set('dialogStatus', 'INVITED');
		setSearchParams(searchParams);
	};

	const btnMenuItems: MenuItemProps[] = [];

	if (userProjectPermissions?.user?.update) {
		if (userStatus !== 'DEACTIVATED') {
			if (frame === 'ENTITY' && (userStatus === 'INVITED' || userStatus === 'CREATED')) {
				btnMenuItems.push({
					children: t('resendInvite'),
					icon: <IconSend />,
					onClick: handleResendInvite,
				});
			}

			btnMenuItems.push({
				children: t('editUser'),
				icon: <IconModeEdit />,
				onClick: handleEditUser,
			});
			btnMenuItems.push({
				children: t('deactivateUser'),
				icon: <IconDoDisturb color="error" />,
				onClick: handleDeactivateUser,
			});

			if (entityId) {
				btnMenuItems.push({
					children: t('removeUser'),
					icon: <IconPersonRemove color="error" />,
					onClick: handleRemoveUser,
				});
			}
		} else {
			btnMenuItems.push({
				children: t('activateUser'),
				icon: <IconPowerSettingsNew color="success" />,
				onClick: handleActivateUser,
			});
		}
	}

	return (
		<>
			<Paper
				sx={(theme) => {
					return {
						border: `1px transparent solid`,
						position: 'relative',
					};
				}}>
				{btnMenuItems.length > 0 && (
					<Box
						sx={(theme) => ({
							position: 'absolute',
							top: '50%',
							right: theme.spacing(1),
							transform: 'translateY(-50%)',
							zIndex: 1,
						})}>
						<BtnMenu items={btnMenuItems} />
					</Box>
				)}
				<Box
					sx={(theme) => {
						return {
							padding: theme.spacing(2),
						};
					}}>
					<Grid alignItems="center" container spacing={{ xs: 1, md: 2 }}>
						<Grid item xs={12} lg={3}>
							<Box
								sx={(theme) => {
									return {
										margin: theme.spacing(0, 0, 2, 0),
										[theme.breakpoints.up('lg')]: {
											margin: 0,
										},
									};
								}}>
								<Grid alignItems="center" container spacing={2} wrap="nowrap">
									<Grid item>
										<IconCircle type="user" />
									</Grid>
									<Grid item flex={1}>
										<TypographyCaption id={user.id} />
										<Typography component="div" fontWeight={600}>
											{user.firstName} {user.lastName}
										</Typography>
									</Grid>
								</Grid>
							</Box>
						</Grid>
						<Grid item xs={12} lg={2}>
							<RowKeyValue
								label={t('jobTitle')}
								value={<Typography>{user.title || t('na')}</Typography>}
							/>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue label={t('email')} value={<LinkEmail email={user.email} />} />
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('phone')}
								value={
									<LinkPhone phone={user.phone} phoneCountry={(user.country as Country)?.phone} />
								}
							/>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue label={t('status')} value={<ChipStatus status={userStatus} />} />
						</Grid>
						<Grid item xs={12} lg={2.5}>
							<RowKeyValue label={t('lastUpdated')} value={<Time date={lastUpdated} />} />
						</Grid>
					</Grid>
				</Box>
			</Paper>
			<DialogUpdateUserStatus
				frame={frame}
				isOpen={paramDialog === 'updateUserStatus' && paramDialogUser === String(user.id)}
				onClose={() => {
					searchParams.delete('dialog');
					searchParams.delete('dialogUser');
					setSearchParams(searchParams);
				}}
				onSuccess={() => {
					if (refetchUsers) {
						refetchUsers();
					}
				}}
				user={user}
			/>
			<DialogEditUser
				frame={frame}
				isOpen={paramDialog === 'editUser' && paramDialogUser === String(user.id)}
				onClose={() => {
					searchParams.delete('dialog');
					searchParams.delete('dialogUser');
					setSearchParams(searchParams);
				}}
				onSuccess={() => {
					if (refetchUsers) {
						refetchUsers();
					}
				}}
				user={user}
			/>
			{entityId && (
				<DialogUpdateUserEntities
					action="REMOVE"
					entity={Number(entityId)}
					frame={frame}
					isOpen={paramDialog === 'removeEntityUser' && paramDialogUser === String(user.id)}
					onClose={() => {
						searchParams.delete('dialog');
						searchParams.delete('dialogUser');
						setSearchParams(searchParams);
					}}
					onSuccess={() => {
						if (refetchUsers) {
							refetchUsers();
						}
					}}
					user={user}
				/>
			)}
		</>
	);
};
